<template>
  <div v-if="isMobile">
    <Menu v-slot="{ open, close }">
      <transition
        v-if="open"
        enter-active-class="duration-200 ease-out"
        enter-from-class="transform opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="transform opacity-0"
        appear
      >
        <div
          :class="[
            'fixed top-0 left-0 z-9999 flex h-full w-screen justify-center bg-neutral-950/70 md:hidden',
          ]"
        />
      </transition>

      <MenuButton as="div">
        <slot name="menu-button">
          <TIcon name="more" />
        </slot>
      </MenuButton>

      <Teleport to="body">
        <MenuItems
          class="border-primary-200 bg-white-500 shadow-2 absolute bottom-0 left-0 z-9999 flex max-h-[calc(66vh)] w-full flex-col divide-y overflow-y-auto rounded-md rounded-b-none border"
        >
          <TContextActionMenuItem
            v-for="item in items"
            :key="item.name"
            :item="item"
          >
          </TContextActionMenuItem>
          <slot :open="open" :close="close" />
        </MenuItems>
      </Teleport>
    </Menu>
  </div>

  <div v-else>
    <Menu v-slot="{ open, close }">
      <Teleport v-if="open" to="body">
        <div
          class="fixed top-0 left-0 z-9999 h-screen w-screen overflow-y-auto"
        >
          <MenuItems
            ref="floating"
            :style="floatingStyles"
            class="border-primary-200 bg-white-500 shadow-2 fixed z-9999 flex w-fit flex-col divide-y rounded-md border"
          >
            <TContextActionMenuItem
              v-for="item in items"
              :key="item.name"
              :item="item"
            >
            </TContextActionMenuItem>
            <slot :open="open" :close="close" />
          </MenuItems>
        </div>
      </Teleport>

      <MenuButton ref="anchor" as="div" data-testid="context-menu" @click.stop>
        <slot name="menu-button">
          <TIcon name="more" />
        </slot>
      </MenuButton>
    </Menu>
  </div>
</template>

<script lang="ts" setup>
import { flip, offset, shift, useFloating } from '@floating-ui/vue';
import { Menu, MenuButton, MenuItems } from '@headlessui/vue';
import { ref } from 'vue';
import { useResponsive } from '../composables/useResponsive';
import TIcon from '../Icon/TIcon.vue';
import { type ContextActionMenuItem } from './context-action-menu-item';
import TContextActionMenuItem from './TContextActionMenuItem.vue';

// for some reason the vue runtime type checker fails when using the `Placement` type from floating UI directly, so we have to duplicate the typeinformation we need :/
// if you need more placements, just add them to this type and make sure they are valid Placement types from @floating-ui/vue
type Placement = 'bottom' | 'bottom-end' | 'right-start';

const { items = [], placement = 'bottom' } = defineProps<{
  items?: ContextActionMenuItem[];
  placement?: Placement;
}>();

const { isMobile } = useResponsive();

const anchor = ref(null);
const floating = ref(null);
const { floatingStyles } = useFloating(anchor, floating, {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  middleware: [offset(4), shift(), flip()],
  placement,
});
</script>
